import { observer } from 'mobx-react'
import React, { useContext, useEffect, useState } from 'react'

import Dialog from '@/components/shared/ui/Dialog'
import {
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from '@/components/shared/ui/Dialog/Dialog'
import Tabs from '@/components/shared/ui/Tabs'
import {
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/shared/ui/Tabs/Tabs'
import HNContext from '@/context/HNContext'
import { useTranslations } from '@/hooks/useTranslations'
import { EventEmitter } from '@/lib/eventEmitter'
import postStore from '@/stores/PostListStore'
import type { IPost } from '@/types/post'
import type { IPostAddon } from '@/types/post/postAddon'

import PushToButton from '../PushToButton'
import CreateClickUpTask from './CreateClickUpTask'
import LinkClickUpTask from './LinkClickUpTask'

interface IPropTypes {
  post: IPost
}
const PushToClickUp = observer(({ post }: IPropTypes) => {
  const t = useTranslations(`post.integrations.clickUp`)
  const { integrations } = useContext(HNContext)
  const [show, setShow] = useState(false)
  const [canCreate, setCanCreate] = useState<boolean>(
    Boolean(post.can_push_to_click_up)
  )

  const handleCreate = (task: IPostAddon) => {
    if (task.action === 'push') setCanCreate(false)
    EventEmitter.dispatch('NEW_POST_ADDON', {
      provider: task.provider,
      addon: task,
      action: task.action,
    })
    setShow(false)
    postStore.updateSinglePost(post.slug, {
      ...post,
      can_push_to_click_up: false,
    })
  }

  useEffect(() => setCanCreate(Boolean(post?.can_push_to_click_up)), [post])

  if (!integrations?.can_manage_click_up) return null

  return (
    <Dialog open={show} onOpenChange={setShow}>
      <DialogTrigger asChild>
        <PushToButton
          onClick={() => setShow(true)}
          provider={`click_up`}
          pushText={t(`pushToClickUp`)}
          linkText={t(`linkToClickUp`)}
          canCreate={Boolean(canCreate)}
          onChangeCanCreate={(value: boolean) => setCanCreate(value)}
        />
      </DialogTrigger>
      <DialogContent size='lg' backdrop>
        <DialogTitle>{t(`title`)}</DialogTitle>
        <DialogDescription>{t(`description`)}</DialogDescription>
        <Tabs defaultValue={canCreate ? 'create' : 'link'}>
          <TabsList>
            {canCreate && (
              <TabsTrigger value='create'>
                <span>{t(`create.title`)}</span>
              </TabsTrigger>
            )}
            <TabsTrigger value='link'>
              <span>{t(`link.title`)}</span>
            </TabsTrigger>
          </TabsList>
          {canCreate && (
            <TabsContent value='create'>
              <CreateClickUpTask post={post} onLink={handleCreate} />
            </TabsContent>
          )}
          <TabsContent value='link'>
            <LinkClickUpTask post={post} onLink={handleCreate} />
          </TabsContent>
        </Tabs>
        <DialogClose />
      </DialogContent>
    </Dialog>
  )
})

export default PushToClickUp
